import { Form, Input, message, Modal, Select } from 'antd';
import { useForm, useWatch } from 'antd/lib/form/Form';
import { useTranslation } from 'react-i18next';
import { Link } from 'tdesign-react';
import './index.scss';
import ExtendWindowIcon from 'assets/images/extend-window.svg';
import { t } from 'i18next';
import { operateThirdPartyTimbre } from 'apis';
import { useEffect } from 'react';

type IProps = {
  visible: boolean;
  onCancel: () => void;
  onConfirm?: () => void;
  detail?: FormData;
};

type FormData = {
  source: string;
  timbreID: string;
  timbreName: string;
  timbreType: string;
  gender: number;
  timbreKey?: string;
};
const formItemLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 17 },
};

const GenderOptionList = [
  { label: t('男'), value: 1 },
  { label: t('女'), value: 2 },
];

const SourceOptionList = [
  { label: t('微软Azure'), value: 'azure' },
  { label: t('Google TTS'), value: 'google' },
];

const ImportThirdPartyTimbre = (props: IProps) => {
  const { t } = useTranslation();
  const [form] = useForm<FormData>();
  const { visible, onCancel, onConfirm, detail } = props;
  const source = useWatch('source', form);
  const onOk = () => {
    form.validateFields().then(async (values) => {
      await operateThirdPartyTimbre({
        ...values,
        operateType: !!detail?.timbreKey ? 'update' : 'insert',
        timbreKey: detail?.timbreKey,
      });
      message.success(t('导入成功'));
      onConfirm?.();
    });
  };
  useEffect(() => {
    if (visible) {
      form.resetFields();
      detail && form.setFieldsValue(detail);
    }
  }, [detail, visible]);
  const handleSourceChange = () => {
    form.setFieldsValue({
      timbreID: '',
    });
  };
  return (
    <Modal
      title={t('请输入导入的音色信息')}
      visible={visible}
      onCancel={onCancel}
      onOk={onOk}
      width={700}
      className="key-edit-modal"
      centered
    >
      <Form form={form} colon={false} labelAlign="left" {...formItemLayout}>
        <Form.Item
          label={t('TTS 提供方')}
          name="source"
          required={true}
          extra={
            source === 'tencent' ? (
              <p>
                <span>{t('若您在')}</span>
                <Link theme="primary" href={'https://cloud.tencent.com/product/tts'} target="_blank">
                  {t('腾讯云TTS')}
                  <img src={ExtendWindowIcon} />
                </Link>
                <span>{t('单独复刻的音色，可以在此处导入，以便在数智人服务中调用。')}</span>
              </p>
            ) : null
          }
        >
          <Select options={SourceOptionList} disabled={!!detail?.timbreKey} onChange={handleSourceChange}/>
        </Form.Item>
        <Form.Item
          label={t('音色命名')}
          name="timbreName"
          required={true}
          rules={[{ min: 2, message: t('至少输入2个字符') }]}
          extra={source === 'google' ? <p>{t('请为此音色设置一个易于识别的别名或显示名称。')}</p> : null}
        >
          <Input />
        </Form.Item>
        <Form.Item label={t('性别')} name="gender" required={true}>
          <Select options={GenderOptionList} />
        </Form.Item>
        {(source === 'azure' || source === 'google') && (
          <Form.Item
            label={source === 'azure' ? t('音色ID') : t('音色名称1')}
            name="timbreID"
            required={true}
            rules={
              source === 'azure'
                ? [{ pattern: /^[a-zA-Z0-9-]+$/, message: t('仅允许字母、数字和横线') }]
                : [
                    { pattern: /^[a-zA-Z0-9-]+$/, message: t('仅允许字母、数字和横线') },
                    {
                      pattern: /Chirp3-HD/i,
                      message: t('当前仅支持使用“Chirp3：高清语音”类型的音色，请确保音色名称中包含“Chirp3-HD”'),
                    },
                  ]
            }
            extra={
              source === 'azure' ? (
                <p>
                  <span>{t('可参考')}</span>
                  <Link
                    theme="primary"
                    href="https://docs.azure.cn/zh-cn/ai-services/speech-service/language-support?tabs=tts'"
                    target="_blank"
                  >
                    {t('语言与声音支持')}
                    <img src={ExtendWindowIcon} />
                  </Link>
                  <span>{t('填写')}</span>
                </p>
              ) : (
                <div>
                  {t('仅支持使用“Chirp3：高清语音”类型的音色，请确保音色名称中包含“Chirp3-HD”，具体可参考')}
                  <Link
                    theme="primary"
                    href="https://cloud.google.com/text-to-speech/docs/list-voices-and-types"
                    target="_blank"
                  >
                    {t('语言与语音支持')}
                    <img src={ExtendWindowIcon} />
                  </Link>
                  {t('列表填写。')}
                </div>
              )
            }
          >
            <Input maxLength={50} showCount />
          </Form.Item>
        )}
        {source === 'tencent' && (
          <>
            <Form.Item
              label="VoiceType"
              name="timbreType"
              required={true}
              rules={[{ pattern: /^\d+$/, message: t('请输入数字类型') }]}
              extra={
                <p>
                  <span>{t('若为一句话版声音复刻时，该值为固定值"200000000"；其他类型声音复刻可在')}</span>
                  <Link theme="primary" href={'https://cloud.tencent.com/product/tts'} target="_blank">
                    {t('控制台')}
                    <img src={ExtendWindowIcon} />
                  </Link>
                  <span>{t('查看。')}</span>
                </p>
              }
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="FastVoiceType"
              name="timbreID"
              rules={[{ pattern: /^[a-zA-Z0-9-]+$/, message: t('仅允许字母、数字和横线') }]}
              extra={
                <p>
                  <span>{t('若为一句话版声音复刻时，须填写该值。')}</span>
                </p>
              }
            >
              <Input />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};
export default ImportThirdPartyTimbre;
